import {
    Badge,
    Card,
    CardHeader,
    makeStyles,
    TableCellLayout,
    type TableColumnDefinition,
    Text,
    Tooltip,
} from '@fluentui/react-components';
import { memo, useMemo } from 'react';
import { graphql, usePreloadedQuery } from 'react-relay';
import { useMediaQuery } from 'usehooks-ts';

import { createTableColumns } from '../list/column/column-util';
import { QueryList } from '../list/QueryList';
import { ProcessDefinitionListLink } from '../process-definition/process-definition-list-link';

import type { processDefinitionsQuery } from './__generated__/processDefinitionsQuery.graphql';
import type {
    processDefinitionsQueryFragment$data,
    Status,
} from './__generated__/processDefinitionsQueryFragment.graphql';

import { processDefinitionsRouteApi } from './process-definitions-api';
import {
    ProcessDefinitionsQueryCtx,
    useProcessDefinitionsQueryFragmentRef,
} from './process-definitions-query';

import { toolbarItems } from './process-definitions-toolbar';

import {
    AddSquare20Regular,
    CircleOff20Regular,
    Edit20Regular,
    Save20Regular,
} from '@fluentui/react-icons';

const columnSizingOptions = {
    dirtyFlag: {
        minWidth: 24,
        defaultWidth: 24,
        idealWidth: 24,
        maxWidth: 24,
    },
    startWhen: {
        minWidth: 24,
        defaultWidth: 24,
        idealWidth: 24,
        maxWidth: 24,
    },
};

export const ProcessDefinitions = memo(() => {
    const isTablet = useMediaQuery('(max-width: 1100px)');
    const isMobile = useMediaQuery('(max-width: 768px)');

    const processDefinitions = usePreloadedQuery<processDefinitionsQuery>(
        query,
        processDefinitionsRouteApi.useLoaderData()
    ).getProcessDefinitions;

    const queryFragment = useProcessDefinitionsQueryFragmentRef(processDefinitions);
    const items = queryFragment?.content ?? [];

    // 🔥 Tablet: reduzierte Spalten
    const responsiveColumns = useMemo(() => {
        if (isTablet) {
            return columns.filter(
                (c) => c.columnId !== 'definitionKey' && c.columnId !== 'definitionVersion'
            );
        }

        return columns;
    }, [isTablet]);

    return (
        <ProcessDefinitionsQueryCtx.Provider value={processDefinitions}>
            {isMobile ? (
                <ProcessDefinitionCards items={items} />
            ) : (
                <QueryList
                    columnSizingOptions={columnSizingOptions}
                    columns={responsiveColumns}
                    items={items}
                    pagination={queryFragment?.pageInfo}
                    routeApi={processDefinitionsRouteApi}
                    selectionMode="multiselect"
                    toolbar={toolbarItems}
                />
            )}
        </ProcessDefinitionsQueryCtx.Provider>
    );
});
ProcessDefinitions.displayName = 'ProcessDefinitions';

const query = graphql`
    query processDefinitionsQuery(
        $pageNum: Int
        $pageSize: Int
        $sortBy: String
        $sortDir: SortDirection
        $search: String
        $searchBy: [String]
        $filter: ProcessDefinitionFilterInput
    ) {
        getProcessDefinitions(
            pageNum: $pageNum
            pageSize: $pageSize
            sortBy: $sortBy
            sortDir: $sortDir
            search: $search
            searchBy: $searchBy
            filterInput: $filter
        ) {
            ...processDefinitionsQueryFragment
        }
    }
`;

type TProcessDefinitions = processDefinitionsQueryFragment$data['content'][number][];

type TProcessDefinition = TProcessDefinitions[number];

const columns: TableColumnDefinition<TProcessDefinition>[] = createTableColumns<TProcessDefinition>(
    {
        columnId: 'dirtyFlag',
        renderCell: (item) =>
            item.dirtyFlag && item.status === 'ACTIVE' ? <Edit20Regular /> : null,
        renderHeaderCell: '',
    },
    {
        columnId: 'startWhen',
        renderCell: (item) => getStartWhenIcon(item.startWhen),
        renderHeaderCell: '',
    },
    {
        columnId: 'id',
        renderCell: (item) => (
            <TableCellLayout>
                <ProcessDefinitionListLink>{item}</ProcessDefinitionListLink>
            </TableCellLayout>
        ),
        renderHeaderCell: 'Prozessname',
    },
    {
        columnId: 'listName',
        renderHeaderCell: 'Liste',
    },
    {
        columnId: 'status',
        renderCell: ({ status }) => <TableCellLayout>{translateStatus(status)}</TableCellLayout>,
        renderHeaderCell: 'Status',
    },
    {
        columnId: 'definitionVersion',
        renderHeaderCell: 'Version',
    },
    {
        columnId: 'definitionKey',
        renderHeaderCell: 'Prozess-ID',
    }
);

const useCardStyles = makeStyles({
    container: {
        display: 'grid',
        gap: '1rem',
        padding: '1rem',
    },
    card: {
        padding: '1rem',
    },
    row: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        //marginTop: '0.5rem',
    },
});

const ProcessDefinitionCards = ({ items }: { items: readonly TProcessDefinition[] }) => {
    const styles = useCardStyles();

    return (
        <div className={styles.container}>
            {items.map((item) => (
                <Card key={item.id} className={styles.card}>
                    <CardHeader
                        header={<ProcessDefinitionListLink>{item}</ProcessDefinitionListLink>}
                        description={`Liste: ${item.listName}`}
                    />

                    <div className={styles.row}>
                        <Badge appearance="outline">{translateStatus(item.status)}</Badge>
                        {item.dirtyFlag && item.status === 'ACTIVE' && <Edit20Regular />}
                    </div>

                    <div className={styles.row}>
                        <Text size={200}>{item.definitionKey}</Text>
                        {getStartWhenIcon(item.startWhen)}
                    </div>
                </Card>
            ))}
        </div>
    );
};

const translateStatus = (status: Status): string => {
    switch (status) {
        case 'ACTIVE':
            return 'Aktiv';
        case 'ARCHIVED':
            return 'Archiviert';
        case 'DRAFT':
            return 'Entwurf';
        case 'INACTIVE':
            return 'Inaktiv';
        case 'TEMPLATE':
            return 'Vorlage';
        default:
            return status;
    }
};

export const getStartWhenIcon = (startWhen: string) => {
    switch (startWhen) {
        case 'NO_START':
            return (
                <Tooltip content="Nicht automatisch starten" relationship="label">
                    <CircleOff20Regular />
                </Tooltip>
            );
        case 'ITEM_ADDED':
            return (
                <Tooltip content="Starte wenn Item erstellt" relationship="label">
                    <AddSquare20Regular />
                </Tooltip>
            );
        case 'ITEM_UPDATED':
            return (
                <Tooltip content="Starte wenn Item aktualisiert" relationship="label">
                    <Save20Regular />
                </Tooltip>
            );
        default:
            return null;
    }
};
